/**
 * PayStack Payment Component for Nigerian Users
 * Handles PayStack payment initialization and processing
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaystackPaymentButton = ({ 
  subscriptionTier,
  subscriptionType = 'monthly',
  originUrl, 
  user,
  onSuccess,
  onError,
  forceChannel = null,   // 'bank' | 'card' | null (let backend decide)
  isFirstPayment = null,  // override for backend channel decision
  buttonClassName,
  buttonLabel,
  buttonIcon,
}) => {
  const [loading, setLoading] = useState(false);
  const [paystackConfig, setPaystackConfig] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  // Load PayStack configuration on mount
  useEffect(() => {
    const loadPaystackConfig = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/payments/paystack/config`);
        const data = await response.json();
        
        setIsConfigured(data.is_configured);
        setPaystackConfig(data);
        
        // Load PayStack inline script if configured
        if (data.is_configured && !window.PaystackPop) {
          const script = document.createElement('script');
          script.src = 'https://js.paystack.co/v1/inline.js';
          script.async = true;
          document.body.appendChild(script);
        }
      } catch (error) {
        logger.error('Failed to load PayStack config:', error);
        setIsConfigured(false);
      }
    };

    loadPaystackConfig();
  }, []);

  const handlePayment = async () => {
    if (!isConfigured) {
      toast.error('PayStack payment is currently unavailable. Please try Stripe instead.');
      return;
    }

    setLoading(true);

    try {
      // Initialize payment on backend
      const response = await fetch(`${BACKEND_URL}/api/payments/paystack/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription_tier: subscriptionTier,
          subscription_type: subscriptionType,
          origin_url: originUrl,
          user_country: 'NG',
          ...(isFirstPayment !== null ? { is_first_payment: isFirstPayment } : {}),
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to initialize payment');
      }

      const result = await response.json();
      const { reference, amount_naira } = result.data;

      // Check if PayStack script is loaded
      if (!window.PaystackPop) {
        throw new Error('PayStack script not loaded. Please refresh the page.');
      }

      // Initialize PayStack payment popup
      const setupOpts = {
        key: paystackConfig.public_key,
        email: user?.email || 'customer@example.com',
        amount: amount_naira * 100, // Amount in kobo
        currency: 'NGN',
        ref: reference,
        onClose: function() {
          toast.info('Payment window closed');
          setLoading(false);
        },
        callback: async function(response) {
          // Verify payment on backend
          try {
            const verifyResponse = await fetch(`${BACKEND_URL}/api/payments/paystack/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                reference: response.reference
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            const verifyResult = await verifyResponse.json();
            
            if (verifyResult.data.payment_status === 'completed') {
              // Round 2 — e-mandate-lite confirmation: gently confirm to the user that
              // the bank just used will now be on file for future renewals.
              const channelUsed = verifyResult.data?.authorization?.channel || forceChannel;
              if (channelUsed === 'bank' || channelUsed === 'bank_transfer') {
                toast.success('Payment received. Your bank is now saved for automatic renewals — you can manage this from Settings.', { duration: 8000 });
              } else {
                toast.success('Payment successful!');
              }
              onSuccess && onSuccess(verifyResult.data);
            } else {
              throw new Error('Payment not completed');
            }
          } catch (error) {
            toast.error('Payment verification failed');
            onError && onError(error);
          } finally {
            setLoading(false);
          }
        }
      };
      // Channel restriction at the PayStack widget — forceChannel keeps the picker locked
      // to a single tile so the two-button UX is honoured.
      if (forceChannel === 'bank') setupOpts.channels = ['bank', 'bank_transfer'];
      if (forceChannel === 'card') setupOpts.channels = ['card'];
      const handler = window.PaystackPop.setup(setupOpts);

      // Open PayStack payment popup
      handler.openIframe();
      
    } catch (error) {
      logger.error('PayStack payment error:', error);
      toast.error(error.message || 'Payment initialization failed');
      onError && onError(error);
      setLoading(false);
    }
  };

  if (!isConfigured) {
    return null; // Don't show PayStack button if not configured
  }

  return (
    <Button
      onClick={handlePayment}
      disabled={loading}
      className={buttonClassName || "w-full bg-linear-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-all"}
      data-testid={forceChannel ? `paystack-pay-${forceChannel}` : 'paystack-pay'}
    >
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          {buttonIcon}
          {buttonLabel || 'Pay with PayStack (₦)'}
        </>
      )}
    </Button>
  );
};

export default PaystackPaymentButton;
